/* eslint-disable @typescript-eslint/consistent-type-assertions */
/* eslint-disable @typescript-eslint/no-inferrable-types */
import { delay } from 'dva/saga';
import { acsFetch } from '@mos-utils/acs-fetch';
import { t } from '@lingui/macro';

import areaDistricts, {
  Area,
  District,
  FIAS_LEVEL_HOUSE,
  isNewMoscowArea,
  ZEL_AO,
} from '../constants/address';
import { getTextValue, getValue } from '../constants/getFieldValues';
import { AddressFields } from '../../types/address';
import { DEFAULT_FETCH_OPTIONS } from '../models/auth';
import { EIRCRequestError } from '../errors/EIRC';
import { DELAY_TIME } from '../constants/delayTime';
import { OMKError, OMKRequestError } from '../errors/OMK';
import { i18n } from './i18n';

/**
 *
 * @param {Area.short} area
 * @returns {District[]}
 */
export const getDistricts = (
  area?: string
): { text: string; value: string; areaId: string }[] => {
  if (!area) {
    return areaDistricts.reduce(
      (totalDistricts: [], { districts = [], short: areaId }: Area) => {
        const newDistricts = districts.map((text, index) => ({
          text,
          value: areaId + index,
          areaId,
        }));
        return totalDistricts.concat(newDistricts as []);
      },
      []
    );
  }

  const { districts, short: areaId = '' } = areaDistricts.find(
    ({ short: areaId }) => areaId === area
  ) || { districts: [] };
  return (districts as District[]).map((text, index) => ({
    text,
    value: areaId + index,
    areaId,
  }));
};
/**
 *
 * @param {string} [areaName = '']
 * @returns {Area.full | Area.short | ''}
 */
export const getAreaShortName = (
  areaName: string = ''
): Area['full'] | Area['short'] | '' => {
  if (!areaName) {
    return '';
  }
  const { short } = areaDistricts.find(({ full, short }) =>
    [full.toLowerCase(), short.toLowerCase()].some(
      (name) => name === areaName.toLowerCase()
    )
  ) || { short: '' };
  return short;
};

export function getAreaNameByDistrict(district: string): string {
  return (
    areaDistricts.find(({ districts }) => districts.includes(district)) || {
      short: '',
    }
  ).short;
}

export function getAddressValueFromSuggest(payload): string {
  const { city_with_type = '', village = '', street_with_type = '' } = payload;
  return [city_with_type, village, street_with_type].filter(Boolean).join(', ');
}

export function getStreetValueFromSuggest(payload): string {
  const {
    city = '',
    city_with_type = '',
    village = '',
    street_with_type = '',
  } = payload;
  return [
    city === 'Зеленоград' ? '' : city_with_type,
    village,
    street_with_type,
  ]
    .filter(Boolean)
    .join(', ');
}

export const checkAddress = (state: AddressFields): boolean => {
  const area = getTextValue(state.area);
  const isNewMoscow = isNewMoscowArea(area);
  const has_OMK = [
    isNewMoscowArea(area) ? true : state.omk_street_code,
    state.omk_district_code,
    state.omk_area_code,
  ].every(Boolean);

  if (state.fiasLevel === FIAS_LEVEL_HOUSE) {
    return has_OMK;
  }

  const validHouse = getValue(state.house) || getValue(state.building);
  const streetValue = getValue(state.street).trim();
  const checkStreetValidation = () => {
    if (state.isManualInput) {
      if (area === ZEL_AO)
        return streetValue.length
          ? state.street.isFoundInCatalog
          : getValue(state.building);
      if (isNewMoscow) return streetValue.length;
      return state.street.isFoundInCatalog;
    }
    return area === ZEL_AO || streetValue;
  };

  const validStreet = checkStreetValidation();

  return [
    has_OMK,
    area,
    getTextValue(state.district),
    validStreet,
    validHouse,
  ].every(Boolean);
};

export function* checkBtiCode(fias_id) {
  const response = yield acsFetch(
    `/api/address-search/v2/address/eirc/check?fias_id=${fias_id}`,
    DEFAULT_FETCH_OPTIONS
  ).catch((error) => {
    throw new EIRCRequestError(error.message);
  });
  try {
    const {
      code,
      data: { serve_by_eirc: isServeByEirc },
    } = yield response;
    if (code !== 'SUCCESS') {
      throw new EIRCRequestError('Не удалось проверить ЕИРЦ');
    }
    if (typeof isServeByEirc !== 'boolean') {
      throw new EIRCRequestError('Не удалось проверить ЕИРЦ');
    }
    return isServeByEirc;
  } catch (e) {
    throw new EIRCRequestError('Не удалось проверить ЕИРЦ');
  }
}

export function* getAddressSuggestions(searchString) {
  yield delay(DELAY_TIME);
  const MOSCOW_REGION = 'Москва';
  const response = yield acsFetch(
    `/api/address-search/v2/address?query=${encodeURIComponent(
      searchString.trim()
    )}&count=${COUNT_OF_SUGGESTS}&regions=${MOSCOW_REGION}`,
    DEFAULT_FETCH_OPTIONS
  );
  if (response.status === 500) {
    throw new Error();
  }
  const { data = [] } = yield response;
  return data;
}

export function* getStreetSuggestions(searchString, isZelAO) {
  yield delay(DELAY_TIME);

  const query = `${encodeURIComponent(
    searchString
  )}&region=${encodeURIComponent('Москва')}${
    isZelAO ? `&city=${encodeURIComponent('Зеленоград')}` : ''
  }`;
  const response = yield acsFetch(
    `/api/address-search/v1/street?query=${query}&count=${COUNT_OF_SUGGESTS}`,
    DEFAULT_FETCH_OPTIONS
  );
  if (response.status === 500) {
    throw new Error();
  }
  const { data = [] } = yield response;
  return data;
}

export const setStreetError = (address: AddressFields): boolean =>
  address.isManualInput && getTextValue(address.area) === ZEL_AO
    ? false
    : (address.street.error as boolean);

export function* getOMKCode(fiasId: string) {
  const response = yield acsFetch(
    `/api/address-search/v1/address/fias/${fiasId}`,
    DEFAULT_FETCH_OPTIONS
  );

  const result = yield response;
  if (result.code !== 'SUCCESS')
    throw new OMKRequestError(
      `Запрос кодов ОМК завершился с кодом ${result.code} и статусом ${response.status}`
    );
  return result;
}

export const setIsAddressCompleted = (address: AddressFields): boolean =>
  address.isAddressCompleted && checkAddress(address);

export const setStreetFoundInCatalog = ({
  isManualInput,
  area,
  suggestions,
  street,
}: AddressFields): boolean =>
  isManualInput &&
  street.value &&
  !isNewMoscowArea(getTextValue(area)) &&
  Boolean(suggestions.length) &&
  suggestions.find(
    (suggest) => getStreetValueFromSuggest(suggest) === street.value
  );

export const getOMKErrorPayload = (error) =>
  error.name === OMKError.errorName
    ? OMKError.errorName
    : OMKRequestError.errorName;

export const COUNT_OF_SUGGESTS = 15;

export enum SPECIAL_VALUE {
  notFound = 1,
  switchToManual,
  error,
  retry,
  loading,
}

export const LOADING = [{ special: SPECIAL_VALUE.loading, visible: '' }];

export const switchToManual = {
  special: SPECIAL_VALUE.switchToManual,
  visible: i18n._(t`Укажите вручную`),
};

export const NOT_FOUND = [
  {
    special: SPECIAL_VALUE.notFound,
    visible: i18n._(t`Не найдено в списке адресов`),
  },
  switchToManual,
];
export const ERROR = [
  {
    special: SPECIAL_VALUE.error,
    visible: i18n._(
      t`По техническим причинам не удалось проверить адрес в списке`
    ),
  },
  { special: SPECIAL_VALUE.retry, visible: i18n._(t`Повторить поиск`) },
];

export function findDistrict(areaName: string, districtName: string): string {
  const filteredByArea: Area[] = areaDistricts.filter(
    (area) => area.short === areaName
  );
  if (filteredByArea && filteredByArea.length) {
    const shortDistrictName = filteredByArea[0].districts.find((district) =>
      districtName.includes(district)
    );
    if (shortDistrictName) return shortDistrictName;
  }
  return '';
}
